import { t } from "@lingui/macro";
import { useCallback, useEffect, useState } from "react";

import { isNativeBiometricSession } from "@whoosh/relay/src/private/nativeSessionPersistence";

import { useFlags } from "../../utils/launchDarkly";
import { useHandleLogOutCleanUp } from "../Authentication/hooks/useHandleLogOutCleanUp";
import {
  enableBiometricUnlock,
  getBiometricUnlockEnabled,
  getBiometricUnlockPromptDeclined,
  getBiometricUnlockType,
  getBiometricUnlockUserId,
  getEnrolledBiometricUnlockTypes,
  isBiometricUnlockAvailable,
  disableBiometricUnlock,
  BiometricUnlockType,
  setBiometricUnlockPromptDeclined,
} from "./biometricUnlock";

type BiometricUnlockState = {
  isAvailable: boolean;
  isEnabled: boolean;
  isLoading: boolean;
  type: BiometricUnlockType;
  enrolledTypes: BiometricUnlockType[];
  hasDeclinedPrompt: boolean;
  errorMessage: string | null;
};

export const useBiometricUnlock = (userId?: string | null) => {
  const { biometricAuth } = useFlags();
  const isFeatureEnabled = biometricAuth === true;
  const handleLogOutCleanUp = useHandleLogOutCleanUp();
  const [state, setState] = useState<BiometricUnlockState>({
    isAvailable: false,
    isEnabled: false,
    isLoading: true,
    type: null,
    enrolledTypes: [],
    hasDeclinedPrompt: false,
    errorMessage: null,
  });

  useEffect(() => {
    let isMounted = true;

    const loadState = async () => {
      try {
        const [
          isAvailable,
          isEnabled,
          storedUserId,
          type,
          enrolledTypes,
          hasDeclinedPrompt,
        ] = await Promise.all([
          isBiometricUnlockAvailable(),
          getBiometricUnlockEnabled(),
          getBiometricUnlockUserId(),
          getBiometricUnlockType(),
          getEnrolledBiometricUnlockTypes(),
          getBiometricUnlockPromptDeclined(),
        ]);

        if (!isMounted) {
          return;
        }

        setState({
          isAvailable,
          isEnabled: isAvailable && isEnabled && storedUserId === userId,
          isLoading: false,
          type,
          enrolledTypes,
          hasDeclinedPrompt,
          errorMessage: null,
        });
      } catch {
        if (!isMounted) {
          return;
        }

        setState({
          isAvailable: false,
          isEnabled: false,
          isLoading: false,
          type: null,
          enrolledTypes: [],
          hasDeclinedPrompt: false,
          errorMessage: null,
        });
      }
    };

    if (isFeatureEnabled) void loadState();

    return () => {
      isMounted = false;
    };
  }, [isFeatureEnabled, userId]);

  const setEnabled = useCallback(
    async (enabled: boolean): Promise<boolean> => {
      if (!isFeatureEnabled) return false;

      if (!enabled) {
        const shouldSignOut = isNativeBiometricSession();
        setState((current) => ({ ...current, isLoading: true }));

        try {
          try {
            await disableBiometricUnlock();
          } finally {
            // Secure storage or revocation failure must not leave a revoked
            // biometric session displayed as authenticated.
            if (shouldSignOut) await handleLogOutCleanUp();
          }
          setState((current) => ({
            ...current,
            isEnabled: false,
            isLoading: false,
          }));
          return true;
        } catch {
          setState((current) => ({ ...current, isLoading: false }));
          return false;
        }
      }

      if (!userId || !state.isAvailable) {
        return false;
      }

      setState((current) => ({
        ...current,
        isEnabled: true,
        isLoading: true,
        errorMessage: null,
      }));

      try {
        await enableBiometricUnlock(userId);
        setState((current) => ({
          ...current,
          isEnabled: true,
          isLoading: false,
        }));
        return true;
      } catch (error) {
        const needsLogin =
          error instanceof Error &&
          "code" in error &&
          error.code === "primary_session_required";
        setState((current) => ({
          ...current,
          isEnabled: false,
          isLoading: false,
          errorMessage: needsLogin
            ? t`Your session has expired. Please sign in again to enable biometric login.`
            : t`We couldn't enable biometric login. Please try again.`,
        }));
        return false;
      }
    },
    [handleLogOutCleanUp, isFeatureEnabled, state.isAvailable, userId]
  );

  const declinePrompt = useCallback(async (): Promise<void> => {
    if (!isFeatureEnabled) return;
    await setBiometricUnlockPromptDeclined();
    setState((current) => ({ ...current, hasDeclinedPrompt: true }));
  }, [isFeatureEnabled]);

  return {
    ...state,
    isAvailable: isFeatureEnabled && state.isAvailable,
    isEnabled: isFeatureEnabled && state.isEnabled,
    isLoading: isFeatureEnabled && state.isLoading,
    declinePrompt,
    setEnabled,
  };
};
